import * as d3 from 'd3';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import { useState, useCallback, useMemo, useEffect, useContext } from 'react';

import IvrTreeContext from 'src/presentation/components/pages/ivr/components/ivrTree/ivrTreeContext';
import { NodeSize } from 'src/presentation/components/pages/ivr/constants/ivrTreeConstants';

export const MIN_SCALE = 0.1;
export const MAX_SCALE = 3;
export const SCALE_STEP = 0.1;

interface Transform {
  k: number;
  x: number;
  y: number;
}

export enum ScreenTransition {
  NOT_SET,
  IN_PROGRESS,
  FINISHED,
}

const useD3 = ({ width, height }) => {
  const { toNode, ivrFrameSize, hideTree } = useContext(IvrTreeContext);

  const [isZoomingScrollingInProgress, setIsZoomingScrollingInProgress] = useState(false);
  const [screenTransitionState, setScreenTransitionState] = useState(ScreenTransition.NOT_SET);
  const [svgTransform, setSvgTransform] = useState('');
  const [transform, setTransform] = useState({ k: 1, x: 0, y: 0 });
  const [transitionToNodeState, setTransitionToNodeState] = useState(ScreenTransition.NOT_SET);
  const [wrapperRef, setWrapperRef] = useState<HTMLDivElement | undefined>();

  const redraw = useCallback(() => {
    if (wrapperRef) {
      const transform = d3.zoomTransform(wrapperRef) as Transform;
      setTransform({ x: Math.round(transform.x), y: Math.round(transform.y), k: transform.k });

      const wrapper = wrapperRef;

      const theSame = Math.round(transform.x) === -wrapper.scrollLeft && Math.round(transform.y) === -wrapper.scrollTop;

      if (!theSame) {
        wrapper.scrollLeft = Math.round(-transform.x);
        wrapper.scrollTop = Math.round(-transform.y);
      }

      const scaledWidth = width * transform.k;
      const scaledHeight = height * transform.k;
      const dx = d3.max([0, wrapper.clientWidth / 2 - scaledWidth / 2]) as number;
      const dy = d3.max([0, wrapper.clientHeight / 2 - scaledHeight / 2]) as number;
      setSvgTransform(`translate(${Math.round(dx)}, ${Math.round(dy)})`);
    }
  }, [wrapperRef, width, height]);

  const zoomBehaviours = useMemo(() => {
    return d3
      .zoom()
      .filter(event => {
        switch (event.type) {
          case 'mousedown':
            return event.button === 0;
          case 'wheel':
            return event.button === 0;
          default:
            return false;
        }
      })
      .scaleExtent([0.1, 3])
      .translateExtent([
        [0, 0],
        [width, height],
      ])
      .on('start', () => setScreenTransitionState(ScreenTransition.IN_PROGRESS))
      .on('zoom', redraw)
      .on('end', () => setScreenTransitionState(ScreenTransition.FINISHED));
  }, [height, redraw, setScreenTransitionState, width]);

  const doWrapperCall = useCallback(
    (transformObject: { x: number; y: number; k: number }): void => {
      let transform = d3.zoomIdentity;

      if (!isNil(transformObject.x) && !isNil(transformObject.y)) {
        transform = transform.translate(transformObject.x, transformObject.y);
      }
      if (!isNil(transformObject.k)) {
        transform = transform.scale(transformObject.k);
      }

      const selector = d3.select(wrapperRef as Element);
      selector.transition().call(zoomBehaviours.transform, transform);
    },
    [wrapperRef, zoomBehaviours]
  );

  const onStart = () => {
    setIsZoomingScrollingInProgress(true);
    if (transitionToNodeState !== ScreenTransition.IN_PROGRESS) {
      setTransitionToNodeState(ScreenTransition.IN_PROGRESS);
    }
  };

  const onEnd = () => {
    setIsZoomingScrollingInProgress(false);
    if (transitionToNodeState === ScreenTransition.IN_PROGRESS) {
      setTransitionToNodeState(ScreenTransition.FINISHED);
    }
  };

  useEffect(() => {
    if (screenTransitionState === ScreenTransition.IN_PROGRESS) {
      onStart();
    } else if (screenTransitionState === ScreenTransition.FINISHED) {
      onEnd();
    }
    // eslint-disable-next-line
  }, [screenTransitionState]); // this is only for integration tests

  const nodeIsVisible = ({ wrapper, node, marginLeft, marginTop }) => {
    const [x, y] = [wrapper.scrollLeft, wrapper.scrollTop];
    const { width, height } = wrapper.getBoundingClientRect();

    const currentTransform = d3.zoomTransform(wrapper);

    const { x: nX = 0, y: nY = 0 } = node;
    const nodeX = (marginLeft + nX) * currentTransform.k;
    const nodeY = (marginTop + nY) * currentTransform.k;

    const isNodeXCoordinateInsideViewPort = x < nodeX && x + width > nodeX;
    const isNodeYCoordinateInsideViewPort = y < nodeY && y + height > nodeY;

    return isNodeXCoordinateInsideViewPort && isNodeYCoordinateInsideViewPort;
  };

  const scrollToNode = useCallback(
    (node, ivrFrameSize) => {
      const wrapper = wrapperRef;
      if (!wrapper) {
        return;
      }

      const currentTransform = d3.zoomTransform(wrapper);

      const nodeX = node.x || 0;
      const nodeY = node.y || 0;

      const marginLeft = get(ivrFrameSize, 'marginLeft', 0);
      const marginTop = get(ivrFrameSize, 'marginTop', 0);
      let x = marginLeft + nodeX;
      let y = marginTop + nodeY;
      x = x * currentTransform.k - wrapper.clientWidth / 2;
      y = nodeY < NodeSize.Default.height ? y * currentTransform.k : y * currentTransform.k - wrapper.clientHeight / 2;

      const isOnTheScreen = nodeIsVisible({ wrapper, node, marginLeft, marginTop });

      if (!isOnTheScreen) {
        doWrapperCall({ x: -x, y: -y, k: currentTransform.k });
      }
    },
    [doWrapperCall, wrapperRef]
  );

  const zoom = useCallback(
    (scaleStep: number) => {
      d3.select(wrapperRef as Element).call(zoomBehaviours.scaleTo, transform.k + scaleStep);
    },
    [wrapperRef, zoomBehaviours.scaleTo, transform]
  );

  const handleZoomIn = useCallback(() => {
    zoom(SCALE_STEP);
  }, [zoom]);

  const handleZoomOut = useCallback(() => {
    zoom(-SCALE_STEP);
  }, [zoom]);

  useEffect(() => {
    if (wrapperRef) {
      d3.select(wrapperRef as Element)
        .call(zoomBehaviours)
        .on('contextmenu', event => {
          event.preventDefault();
        })
        .on('wheel', event => {
          event.preventDefault && event.preventDefault();
          event.deltaY < 0 ? handleZoomIn() : handleZoomOut();
        });
    }
  }, [handleZoomIn, handleZoomOut, wrapperRef, zoomBehaviours]);

  useEffect(() => {
    if (toNode) {
      scrollToNode(toNode, ivrFrameSize);
    }
    // eslint-disable-next-line
  }, [toNode, ivrFrameSize]); // for integration test

  return {
    handleZoomIn,
    handleZoomOut,
    hideTree,
    isZoomingScrollingInProgress,
    setWrapperRef,
    svgTransform,
    transform,
    transitionToNodeState,
  };
};

export default useD3;
